import Image from 'next/image';
import { VideoCardProps } from '@components/v3/molecules/video-cards';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import { Locale } from '@core/constants/locales.constants';
import templateConfig from '@templateConfig';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { Video } from '@graphql/index';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import {
  PLAYLIST_QUERY_PARAM,
  PLAYLIST_SLUG_QUERY_PARAM,
  VIDEO_PUBLISH_DATE_FORMAT,
} from '@core/constants/video.constants';
import {
  getVideoThumbnailUrl,
  getVideoTitle,
} from '@utils/transforms/video/videoFieldFallbacks';

export type VideoCardSettings = {
  dictionary: {
    membersOnly: Translations['videoCard']['label']['membersOnly'];
  };
};

export type VideoCardTransformOptions = {
  playlistContext?: { slug: string };
};

const videoCardTransform = (
  videoEntry: CleanContentful<Video>,
  locale: Locale,
  options: VideoCardTransformOptions = {}
): VideoCardProps => {
  const { video, slug, image } = videoEntry;
  const title = getVideoTitle(videoEntry);
  const thumbnailUrl = getVideoThumbnailUrl(videoEntry);

  return {
    type: 'default',
    category: video?.channelTitle
      ? {
          category: video.channelTitle,
          slug: '#',
          onDark: true,
          disabled: true,
        }
      : undefined,
    slug: generateFullPathname({
      locale,
      route: templateConfig.navigation.routes.VIDEO_DETAIL,
      params: { slug },
      // The detail template detects stream vs YouTube from the fetched
      // Contentful entry (data.video.provider), so we don't need to put it
      // in the URL — only the playlist context query params are needed.
      query: options.playlistContext
        ? {
            [PLAYLIST_QUERY_PARAM]: 'true',
            [PLAYLIST_SLUG_QUERY_PARAM]: options.playlistContext.slug,
          }
        : undefined,
    }),
    image: image ? (
      <ContentfulImage {...image} />
    ) : thumbnailUrl ? (
      <Image
        src={thumbnailUrl}
        alt={title || ''}
        width={0}
        height={0}
        sizes="100vw"
        style={{ width: '100%', height: 'auto' }}
      />
    ) : (
      <Image
        src="/images/search_fallback.png"
        style={{ objectFit: 'cover', objectPosition: 'center' }}
        loading="lazy"
        width={176}
        height={176}
        alt="Search Fallback"
      />
    ),
    publishDate: video?.date
      ? {
          date: new Date(video.date),
          format: VIDEO_PUBLISH_DATE_FORMAT,
          locale,
          onDark: true,
        }
      : undefined,
    title,
    duration: video?.duration
      ? {
          icon: null,
          text: video.duration,
        }
      : undefined,
    onDark: true,
  };
};

export default videoCardTransform;
